import React, { useContext, useRef, useState } from "react";
import styled from "styled-components";
import { PlaybackContext } from "../../state/playbackcontext";
import { media } from "../../styles/theme";

const SPEED_OPTIONS = [0.5, 0.65, 0.75, 0.85, 1, 1.25, 1.5, 1.75, 2];

const PlaybackBar: React.FC = () => {
  const { state, pause, resume, stop, nextChunk, prevChunk, seekToChunk, setPlaybackRate, setVolume, toggleMute, toggleBackgroundMusic, dismiss, scrollToCurrent } = useContext(PlaybackContext);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);

  if (!state.visible) return null;

  const progress = state.totalChunks > 0 ? (state.currentIndex / state.totalChunks) : 0;

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current) return;
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const fraction = clickX / rect.width;
    const targetIndex = Math.floor(fraction * state.totalChunks);
    seekToChunk(Math.max(0, Math.min(targetIndex, state.totalChunks - 1)));
  };

  const truncate = (text: string, max: number) => {
    if (!text) return '';
    const clean = text.replace(/^\s*[0-9\/]+\s*:\s*/, '');
    return clean.length > max ? clean.substring(0, max) + '...' : clean;
  };

  return (
    <BarContainer>
      <ProgressTrack ref={progressRef} onClick={handleProgressClick}>
        <ProgressFill style={{width: `${progress * 100}%`}} />
      </ProgressTrack>

      <BarContent>
        <InfoSection>
          <ScriptureTitle title={state.scriptureTitle}>{truncate(state.scriptureTitle, 30)}</ScriptureTitle>
          <CurrentSentence title={state.currentSentence}>{truncate(state.currentSentence, 50)}</CurrentSentence>
        </InfoSection>

        <ControlsSection>
          <ControlButton onClick={prevChunk} disabled={state.currentIndex <= 0} title="Previous verse">
            ⏮
          </ControlButton>
          <PlayPauseButton onClick={() => state.isPlaying ? pause() : resume()} title={state.isPlaying ? "Pause" : "Play"}>
            {state.isPlaying ? '⏸' : '▶'}
          </PlayPauseButton>
          <ControlButton onClick={stop} title="Stop">
            ⏹
          </ControlButton>
          <ControlButton onClick={nextChunk} disabled={state.currentIndex >= state.totalChunks - 1} title="Next verse">
            ⏭
          </ControlButton>
        </ControlsSection>

        <RightSection>
          <ChunkIndicator>{state.currentIndex + 1} / {state.totalChunks}</ChunkIndicator>

          <SpeedWrapper>
            <ControlButton onClick={() => setShowSpeedMenu(!showSpeedMenu)} title="Playback speed">
              {state.playbackRate}x
            </ControlButton>
            {showSpeedMenu && (
              <SpeedMenu>
                {SPEED_OPTIONS.map(speed => (
                  <SpeedOption key={speed} $active={state.playbackRate === speed} onClick={() => { setPlaybackRate(speed); setShowSpeedMenu(false); }}>
                    {speed}x
                  </SpeedOption>
                ))}
              </SpeedMenu>
            )}
          </SpeedWrapper>

          <VolumeWrapper>
            <VolumeButton onClick={toggleMute} title={state.isMuted ? "Unmute" : "Mute"}>
              <VolumeIcon viewBox="0 0 24 24" width="14" height="14">
                <path d="M3 9v6h4l5 5V4L7 9H3z" fill="currentColor"/>
                {!state.isMuted && state.volume > 0 && <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" fill="currentColor"/>}
                {!state.isMuted && state.volume > 0.5 && <path d="M19 12c0 3.53-2.04 6.58-5 8.05v-2.17c1.81-1.3 3-3.47 3-5.88s-1.19-4.58-3-5.88V4.95c2.96 1.47 5 4.53 5 8.05z" fill="currentColor" opacity="0.6"/>}
                {state.isMuted && <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth="2"/>}
              </VolumeIcon>
            </VolumeButton>
            <VolumeSlider
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={state.isMuted ? 0 : state.volume}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setVolume(v);
                if (state.isMuted && v > 0) toggleMute();
              }}
            />
          </VolumeWrapper>

          <MusicButton onClick={toggleBackgroundMusic} $enabled={state.backgroundMusicEnabled} title={state.backgroundMusicEnabled ? "Disable background music" : "Enable background music"}>
            <svg viewBox="0 0 24 24" width="14" height="14">
              <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" fill="currentColor"/>
              {!state.backgroundMusicEnabled && <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2"/>}
            </svg>
          </MusicButton>

          <ScrollToButton onClick={scrollToCurrent} title="Scroll to current verse">
            <svg viewBox="0 0 24 24" width="14" height="14">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
              <circle cx="12" cy="12" r="3" fill="currentColor"/>
            </svg>
          </ScrollToButton>

          <CloseButton onClick={dismiss} title="Close player">
            ✕
          </CloseButton>
        </RightSection>
      </BarContent>
    </BarContainer>
  );
};

export { PlaybackBar };

// ── Styled Components ──

const BarContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1001;
  background-color: ${props => props.theme.colors.bright};
  border-top: 1px solid ${props => props.theme.colors.gray};
  box-shadow: 0px -4px 12px rgba(0,0,0,0.1);
  overflow: hidden;
  max-width: 100vw;
  box-sizing: border-box;
  @media print { display: none; }

  animation: slideUp 0.35s cubic-bezier(0.32, 0.72, 0, 1);
  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 4px;
  background-color: ${props => props.theme.colors.lightgray};
  cursor: pointer;
  position: relative;

  &:hover {
    height: 6px;
  }
  transition: height 0.15s;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: ${props => props.theme.colors.red};
  transition: width 0.3s ease;
  border-radius: 0 2px 2px 0;
`;

const BarContent = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  gap: 16px;
  max-width: 100%;
  box-sizing: border-box;

  ${media.mobile`
    padding: 6px 8px;
    gap: 4px;
    flex-wrap: wrap;
    justify-content: center;
  `}
`;

const InfoSection = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;

  ${media.mobile`
    flex: 1 1 100%;
    text-align: center;
    order: -1;
  `}
`;

const ScriptureTitle = styled.div`
  font-size: 0.8rem;
  font-weight: bold;
  color: ${props => props.theme.colors.dark};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CurrentSentence = styled.div`
  font-size: 0.7rem;
  color: ${props => props.theme.colors.darkgray};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ControlsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ControlButton = styled.button<{disabled?: boolean}>`
  background: none;
  border: none;
  color: ${props => props.disabled ? props.theme.colors.gray : props.theme.colors.dark};
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  font-size: 0.85rem;
  padding: 4px 6px;
  border-radius: 4px;
  line-height: 1;
  user-select: none;
  opacity: ${props => props.disabled ? 0.4 : 1};
  transition: background-color 0.15s ease, transform 0.15s ease;

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.lightgray};
    transform: scale(1.05);
  }
`;

const PlayPauseButton = styled(ControlButton)`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.red};
  padding: 4px 10px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  ${media.mobile`
    gap: 2px;
  `}
`;

const ChunkIndicator = styled.span`
  font-size: 0.7rem;
  color: ${props => props.theme.colors.darkgray};
  white-space: nowrap;
  min-width: 45px;
  text-align: center;
`;

const SpeedWrapper = styled.div`
  position: relative;
`;

const SpeedMenu = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${props => props.theme.colors.bright};
  border: 1px solid ${props => props.theme.colors.gray};
  border-radius: 6px;
  box-shadow: 0 -4px 12px rgba(0,0,0,0.1);
  padding: 4px 0;
  margin-bottom: 4px;
  z-index: 1002;

  ${media.mobile`
    left: auto;
    right: 0;
    transform: none;
  `}
`;

const SpeedOption = styled.button<{$active: boolean}>`
  display: block;
  width: 100%;
  background: ${props => props.$active ? props.theme.colors.lightgray : 'none'};
  border: none;
  color: ${props => props.$active ? props.theme.colors.red : props.theme.colors.dark};
  font-size: 0.8rem;
  padding: 6px 16px;
  cursor: pointer;
  white-space: nowrap;
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${props => props.theme.colors.lightgray};
  }
`;

const VolumeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  ${media.mobile`
    display: none;
  `}
`;

const VolumeSlider = styled.input`
  width: 60px;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: ${props => props.theme.colors.lightgray};
  border-radius: 2px;
  outline: none;
  cursor: pointer;
  margin: 0;
  padding: 0;
  border: none;

  &::-webkit-slider-runnable-track {
    height: 4px;
    background: ${props => props.theme.colors.lightgray};
    border-radius: 2px;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => props.theme.colors.red};
    cursor: pointer;
    margin-top: -3px;
    border: none;
  }

  &::-moz-range-track {
    height: 4px;
    background: ${props => props.theme.colors.lightgray};
    border-radius: 2px;
    border: none;
  }

  &::-moz-range-thumb {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => props.theme.colors.red};
    cursor: pointer;
    border: none;
  }

  &::-ms-track {
    height: 4px;
    background: transparent;
    border-color: transparent;
    color: transparent;
  }

  &::-ms-thumb {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => props.theme.colors.red};
    border: none;
  }

  &::-ms-fill-lower,
  &::-ms-fill-upper {
    background: ${props => props.theme.colors.lightgray};
    border-radius: 2px;
  }
`;

const VolumeButton = styled(ControlButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
`;

const VolumeIcon = styled.svg`
  display: block;
`;

const MusicButton = styled(ControlButton)<{$enabled: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  opacity: ${props => props.$enabled ? 1 : 0.4};
`;

const ScrollToButton = styled(ControlButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
`;

const CloseButton = styled(ControlButton)`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.darkgray};

  &:hover {
    color: ${props => props.theme.colors.red};
  }
`;
